/**
 * BancoSelect (Bloco BSEL.4).
 *
 * Combobox searchable de bancos brasileiros (snapshot estatico ~270
 * bancos do BACEN/BrasilAPI). Mostra logo+codigo+nome em cada opcao;
 * secao "Mais usados" no topo (top 12 favoritos) + secao "Todos"
 * alfabetica. Search por codigo OU nome OU nomeCurto (sem acentos).
 *
 * Implementacao custom, zero dep externa. Padrao Portal pra escapar
 * de overflow:hidden de ancestrais (mesma estrategia do RailFlyout).
 *
 * @example
 *   <BancoSelect
 *     value="237"
 *     onChange={(banco) => setEstado({
 *       banco_codigo: banco?.codigo || "",
 *       banco_nome: banco?.nome || "",
 *     })}
 *     placeholder="Selecione o banco"
 *   />
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiSearch, FiX } from "react-icons/fi";
import {
  bancosOrdenados,
  buscarBancos,
  obterBanco,
} from "../../utils/bancos";
import BancoAvatar from "./BancoAvatar";
import {
  DropdownPortal,
  EmptyState,
  IconButton,
  ItemBotao,
  ItemCodigo,
  ItemNome,
  ItemNomeLongo,
  ListaScroll,
  SearchBox,
  SearchIcon,
  SearchInput,
  SecaoTitulo,
  Trigger,
  TriggerActions,
  TriggerCodigo,
  TriggerLabel,
  TriggerNome,
  Wrapper,
} from "./styles";

const DROPDOWN_LARGURA_MIN = 320;
const DROPDOWN_OFFSET_Y = 4;

export default function BancoSelect({
  value,
  onChange,
  placeholder = "Selecione o banco",
  disabled = false,
  required = false,
  // Quando true (default), permite limpar via X.
  permitirLimpar = true,
}) {
  const [aberto, setAberto] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [posicao, setPosicao] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const bancoSelecionado = useMemo(() => obterBanco(value), [value]);

  // Lista resolvida em duas secoes (favoritos + todos) ou flat por busca.
  const { favoritos, todos, semBusca } = useMemo(() => {
    if (query.trim()) {
      return {
        favoritos: [],
        todos: buscarBancos(query, 100),
        semBusca: false,
      };
    }
    const { favoritos: f, todos: t } = bancosOrdenados();
    return { favoritos: f, todos: t, semBusca: true };
  }, [query]);

  // Lista plana — usada para keyboard nav e indexacao de highlight.
  const listaPlana = useMemo(
    () => [...favoritos, ...todos],
    [favoritos, todos]
  );

  // Calcula posicao do dropdown (fixed) — vira ancorada no trigger.
  const recalcularPosicao = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const larguraEscolhida = Math.max(rect.width, DROPDOWN_LARGURA_MIN);
    let left = rect.left;
    // Evita vazar pra direita.
    if (left + larguraEscolhida > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - larguraEscolhida - 8);
    }
    setPosicao({
      top: rect.bottom + DROPDOWN_OFFSET_Y,
      left,
      width: larguraEscolhida,
    });
  }, []);

  // Quando abrir, recalcula + foca input + reseta highlight.
  useLayoutEffect(() => {
    if (!aberto) return;
    recalcularPosicao();
    setHighlight(0);
    // foco no proximo tick (depois do dropdown montar)
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [aberto, recalcularPosicao]);

  // Reposiciona em resize/scroll enquanto aberto.
  useEffect(() => {
    if (!aberto) return undefined;
    const handler = () => recalcularPosicao();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [aberto, recalcularPosicao]);

  // Click fora fecha.
  useEffect(() => {
    if (!aberto) return undefined;
    function onDocMouseDown(event) {
      if (triggerRef.current?.contains(event.target)) return;
      if (dropdownRef.current?.contains(event.target)) return;
      setAberto(false);
      setQuery("");
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [aberto]);

  const escolher = useCallback(
    (banco) => {
      onChange?.(banco || null);
      setAberto(false);
      setQuery("");
      // devolve foco ao trigger pra a11y
      triggerRef.current?.focus();
    },
    [onChange]
  );

  const limpar = useCallback(
    (event) => {
      event.stopPropagation();
      if (!permitirLimpar || required) return;
      onChange?.(null);
    },
    [onChange, permitirLimpar, required]
  );

  const onKeyDownTrigger = useCallback((event) => {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setAberto(true);
    }
  }, []);

  const onKeyDownInput = useCallback(
    (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setAberto(false);
        setQuery("");
        triggerRef.current?.focus();
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlight((h) => Math.min(h + 1, listaPlana.length - 1));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        const escolhido = listaPlana[highlight];
        if (escolhido) escolher(escolhido);
      }
    },
    [highlight, listaPlana, escolher]
  );

  // Scroll do item highlighted para dentro da viewport do dropdown.
  useEffect(() => {
    if (!aberto || !dropdownRef.current) return;
    const ativo = dropdownRef.current.querySelector(
      `[data-bsel-item-idx="${highlight}"]`
    );
    if (ativo && typeof ativo.scrollIntoView === "function") {
      ativo.scrollIntoView({ block: "nearest" });
    }
  }, [aberto, highlight]);

  // Render de cada item — extrai indice plano pra navegacao por teclado.
  function renderItem(banco, idxPlano) {
    const ativo = idxPlano === highlight;
    const mostraNomeLongo =
      banco.nome &&
      banco.nomeCurto &&
      banco.nome !== banco.nomeCurto &&
      !banco.nome.includes(banco.nomeCurto);
    return (
      <ItemBotao
        key={banco.codigo}
        type="button"
        data-bsel-item-idx={idxPlano}
        $active={ativo}
        onMouseEnter={() => setHighlight(idxPlano)}
        onClick={() => escolher(banco)}
        role="option"
        aria-selected={value === banco.codigo}
      >
        <BancoAvatar
          codigo={banco.codigo}
          nomeCurto={banco.nomeCurto}
          size={22}
        />
        <ItemCodigo>{banco.codigo}</ItemCodigo>
        <ItemNome>{banco.nomeCurto || banco.nome}</ItemNome>
        {mostraNomeLongo && <ItemNomeLongo>· {banco.nome}</ItemNomeLongo>}
      </ItemBotao>
    );
  }

  const totalEncontrado = listaPlana.length;

  return (
    <Wrapper>
      <Trigger
        ref={triggerRef}
        type="button"
        $vazio={!bancoSelecionado}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-required={required}
        onClick={() => !disabled && setAberto((v) => !v)}
        onKeyDown={onKeyDownTrigger}
      >
        <TriggerLabel>
          {bancoSelecionado ? (
            <>
              <BancoAvatar
                codigo={bancoSelecionado.codigo}
                nomeCurto={bancoSelecionado.nomeCurto}
                size={22}
              />
              <TriggerCodigo>{bancoSelecionado.codigo}</TriggerCodigo>
              <TriggerNome>
                {bancoSelecionado.nomeCurto || bancoSelecionado.nome}
              </TriggerNome>
            </>
          ) : (
            <TriggerNome>{placeholder}</TriggerNome>
          )}
        </TriggerLabel>
        <TriggerActions>
          {bancoSelecionado && permitirLimpar && !required && !disabled && (
            <IconButton
              as="span"
              role="button"
              aria-label="Limpar seleção"
              onMouseDown={(e) => e.preventDefault()}
              onClick={limpar}
            >
              <FiX size={14} />
            </IconButton>
          )}
          <IconButton as="span" aria-hidden="true">
            <FiChevronDown size={14} />
          </IconButton>
        </TriggerActions>
      </Trigger>

      {aberto &&
        createPortal(
          <DropdownPortal
            ref={dropdownRef}
            style={{
              top: posicao.top,
              left: posicao.left,
              width: posicao.width,
            }}
            role="dialog"
          >
            <SearchBox>
              <SearchIcon>
                <FiSearch size={14} />
              </SearchIcon>
              <SearchInput
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por código ou nome..."
                onKeyDown={onKeyDownInput}
                aria-label="Buscar banco"
                aria-autocomplete="list"
                aria-controls="bsel-listbox"
                aria-activedescendant={
                  listaPlana[highlight]
                    ? `bsel-item-${listaPlana[highlight].codigo}`
                    : undefined
                }
              />
            </SearchBox>

            <ListaScroll id="bsel-listbox" role="listbox">
              {totalEncontrado === 0 && (
                <EmptyState>Nenhum banco encontrado.</EmptyState>
              )}

              {semBusca && favoritos.length > 0 && (
                <>
                  <SecaoTitulo>Mais usados</SecaoTitulo>
                  {favoritos.map((banco, idx) => renderItem(banco, idx))}
                </>
              )}

              {semBusca && todos.length > 0 && (
                <>
                  <SecaoTitulo>Todos ({todos.length})</SecaoTitulo>
                  {todos.map((banco, idx) =>
                    renderItem(banco, favoritos.length + idx)
                  )}
                </>
              )}

              {!semBusca &&
                todos.map((banco, idx) => renderItem(banco, idx))}
            </ListaScroll>
          </DropdownPortal>,
          document.body
        )}
    </Wrapper>
  );
}
